import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Gift, Wallet, Trophy, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '../context/AuthContext';
import { flag } from '../config';
import { api, getToken } from '../lib/api';
import { countActiveClaims } from '../lib/claimStatus';
import { bottomNavShown } from './bottomNavRules';

// Where the nav shows (SLOT_MODEL: consumer surfaces only; flag off: the old exclusions and
// their history): components/bottomNavRules.js.
export {
  BOTTOM_NAV_EXCLUDED, BOTTOM_NAV_EXCLUDED_EXACT, BOTTOM_NAV_CONSUMER_PATHS, BUSINESS_PORTAL_SEGMENTS,
  isBottomNavExcluded, isConsumerSurface, bottomNavShown,
} from './bottomNavRules';

/**
 * SLOT_MODEL (Phase 5c) "For you" items: the patron's own places, same count as
 * today's default (Deals · Wallet · Profile). No Rewards tab under the slot model.
 */
export const SLOT_NAV_ITEMS = [
  { id: 'deals', label: 'Deals', route: '/local-deals-near-me' },
  { id: 'wallet', label: 'Wallet', route: '/deal-wallet' },
  { id: 'profile', label: 'Profile', route: '/profile' },
];

const SITE_ICONS = { deals: Gift, wallet: Wallet, profile: User };

/** "8" → "8", 1200 → "1k", 0 / missing → null (the badge is hidden). */
export const formatNavBadge = (value) => {
  if (!value || value <= 0) return null;
  if (value >= 1000) return `${Math.floor(value / 1000)}k`;
  return value.toString();
};

/**
 * The SLOT_MODEL (Sept 2026 redesign) look of the bottom nav: a sheet bar with a 1px rule
 * top edge, navy icons and IBM Plex Mono labels (navy at 70%, 5.8:1; the current tab navy
 * and medium), the current tab marked by a 3px brand-orange bar along its top edge
 * (3.39:1 against sheet) and a paper fill, the Wallet count as the header's badge (orange
 * with navy text, 4.62:1; digits aria-hidden, spoken as ", N active"). A `.site-light`
 * surface: focus rings are rust, drawn inside the tab. Shown below the 800px nav
 * breakpoint. After the fixed bar an in-flow spacer of the same height keeps the end of
 * the page (the footer's last row) from sitting under it.
 */
function SiteBottomNav({ items, pathname, claimsCount }) {
  return (
    <>
      <nav
        aria-label="For you"
        data-site-bottom-nav=""
        className="site-light fixed inset-x-0 bottom-0 z-50 border-t border-brand-rule bg-brand-sheet pb-[env(safe-area-inset-bottom,0px)] text-navy shadow-[0_-3px_0] shadow-navy/5 nav:hidden"
      >
        <ul className="mx-auto flex h-16 max-w-canvas items-stretch">
          {items.map((item) => {
            const active = pathname === item.route;
            const Icon = SITE_ICONS[item.id] || Gift;
            const badgeValue = item.id === 'wallet' ? formatNavBadge(claimsCount) : null;
            return (
              <li key={item.id} className="flex min-w-0 flex-1">
                <Link
                  to={item.route}
                  aria-current={active ? 'page' : undefined}
                  data-active={active ? 'true' : 'false'}
                  className={cn(
                    'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 font-label text-[0.6875rem] uppercase leading-none tracking-[0.07em] no-underline',
                    'transition-colors duration-180 ease-out focus-visible:outline-offset-[-3px]',
                    active ? 'bg-brand-paper font-medium text-navy' : 'text-navy/70 hover:text-navy',
                  )}
                >
                  <span
                    aria-hidden="true"
                    data-indicator=""
                    className={cn('absolute inset-x-3 top-0 h-[3px]', active ? 'bg-brand-orange' : 'bg-transparent')}
                  />
                  <span className="relative">
                    <Icon aria-hidden="true" size={22} strokeWidth={active ? 2.25 : 1.75} />
                    {badgeValue ? (
                      <span
                        aria-hidden="true"
                        data-badge=""
                        className="absolute -right-3 -top-2 grid h-[1.125rem] min-w-[1.125rem] place-items-center rounded-full bg-brand-orange px-1 font-body text-[0.625rem] font-bold leading-none tracking-normal text-navy"
                      >
                        {badgeValue}
                      </span>
                    ) : null}
                  </span>
                  <span>{item.label}</span>
                  {badgeValue ? <span className="sr-only">{`, ${badgeValue} active`}</span> : null}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <div aria-hidden="true" data-bottom-nav-spacer="" className="h-[calc(4rem+env(safe-area-inset-bottom,0px))] nav:hidden" />
    </>
  );
}

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [claimsCount, setClaimsCount] = useState(0);
  const [tapsBalance, setTapsBalance] = useState(0);

  // Signed in, on a path the rules allow (bottomNavRules: SLOT_MODEL = consumer surfaces).
  const shown = bottomNavShown(isAuthenticated, location.pathname);

  // Fetch claims count and Taps balance (only while the nav is on screen)
  useEffect(() => {
    const fetchData = async () => {
      if (!shown) return;

      if (!getToken()) return;

      try {
        // Fetch claims count
        // Active codes only (unredeemed and unexpired), the same rule as the Header badge.
        const { data: claims } = await api.get('/deals/my-claims');
        setClaimsCount(countActiveClaims(claims));

        // Fetch Taps balance (Phase 1: only when REWARDS is on).
        // `total_taps` is the field the endpoint returns — the header reads the
        // same one; this component used to read a `balance` that never existed
        // (audit row 116).
        if (!flag('REWARDS')) return;
        const { data: tapsData } = await api.get('/taps/balance');
        setTapsBalance(tapsData.total_taps || 0);
      } catch {
        // A 401 while the session settles is expected; leave the counts at zero.
      }
    };

    fetchData();
  }, [shown, location.pathname]);

  // Don't render if not authenticated or on a path the rules hide it on
  if (!shown) {
    return null;
  }

  // SLOT_MODEL: the redesigned bar (links, "For you"). Flag off: today's buttons, unchanged.
  if (flag('SLOT_MODEL')) {
    return <SiteBottomNav items={SLOT_NAV_ITEMS} pathname={location.pathname} claimsCount={claimsCount} />;
  }

  const legacyNavItems = [
    {
      id: 'deals',
      label: 'Deals',
      route: '/local-deals-near-me',
      icon: Gift,
      activeColor: '#f97316', // Orange
      activeBg: 'rgba(249, 115, 22, 0.1)'
    },
    {
      id: 'wallet',
      label: 'Wallet',
      route: '/deal-wallet',
      icon: Wallet,
      activeColor: '#f59e0b', // Amber
      activeBg: 'rgba(245, 158, 11, 0.1)',
      badge: claimsCount
    },
    ...(flag('REWARDS') ? [{
      id: 'rewards',
      label: 'Rewards',
      route: '/rewards',
      icon: Trophy,
      activeColor: '#8b5cf6', // Purple
      activeBg: 'rgba(139, 92, 246, 0.1)',
      badge: tapsBalance
    }] : []),
    {
      id: 'profile',
      label: 'Profile',
      route: '/profile',
      icon: User,
      activeColor: '#1e1b4b', // Navy
      activeBg: 'rgba(30, 27, 75, 0.1)'
    }
  ];

  const navItems = legacyNavItems;

  const isActive = (route) => location.pathname === route;

  const formatBadge = formatNavBadge;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
      style={{
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid rgba(30, 27, 75, 0.08)',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      <div className="flex items-center justify-around h-16">
        {navItems.map((item) => {
          const active = isActive(item.route);
          const Icon = item.icon;
          const badgeValue = formatBadge(item.badge);

          const content = (
            <>
              <div className="relative">
                {/* Always explicit: lucide adds its default aria-hidden only when no aria-*
                    prop is passed, and an `undefined` one counts as passed. */}
                <Icon
                  size={22}
                  aria-hidden="true"
                  style={{
                    color: active ? item.activeColor : 'rgba(30, 27, 75, 0.4)',
                    strokeWidth: active ? 2.5 : 2
                  }}
                />
                {badgeValue && (
                  <span
                    className="absolute -top-2 -right-3 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-white"
                    style={{
                      backgroundColor: '#ef4444',
                      fontSize: '10px',
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700
                    }}
                  >
                    {badgeValue}
                  </span>
                )}
              </div>
              <span
                className="mt-1"
                style={{
                  fontSize: '11px',
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: active ? 600 : 500,
                  color: active ? item.activeColor : 'rgba(30, 27, 75, 0.4)'
                }}
              >
                {item.label}
              </span>
            </>
          );
          const common = {
            className: 'flex flex-col items-center justify-center flex-1 h-full relative transition-all duration-200',
            style: { backgroundColor: active ? item.activeBg : 'transparent' },
          };

          return (
            <button
              key={item.id}
              onClick={() => navigate(item.route)}
              {...common}
            >
              {content}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
